import {
    HubConnection,
    HubConnectionBuilder,
    HttpTransportType,
    LogLevel,
    type IRetryPolicy,
} from "@microsoft/signalr";
import {
    InteractionRequiredAuthError,
    type AccountInfo,
    type PublicClientApplication,
} from "@azure/msal-browser";
import type { AppConfig } from "../auth/authConfig";
import { API_PREFIX, InteractionRequiredError } from "../auth/apiClient";

// ---------------------------------------------------------------------------
// Hub path
//
// Resolves through the same `/api/einstein` prefix the REST surface uses.
// Vite's dev server proxy forwards it to the API at :5000; Traefik in
// QA/prod strips the prefix before forwarding to the API pod. The
// negotiate roundtrip then redirects the long-lived WS straight to the
// Azure SignalR Service hostname, so this prefix only governs negotiate
// traffic.
// ---------------------------------------------------------------------------

const REALTIME_HUB_PATH = `${API_PREFIX}/realtime/work`;

// ---------------------------------------------------------------------------
// Token acquisition
//
// Mirrors the silent-acquire-then-throw-InteractionRequired contract in
// `auth/apiClient.ts`. Pulled out here (rather than imported) because
// `accessTokenFactory` returns a bare token string, not a Bearer header —
// reusing `apiClient.fetch` would mean parsing headers out of a Response we
// don't actually need to issue.
//
// `accessTokenFactory` is invoked by the SignalR client on every connect
// AND every reconnect, so the function naturally lifts the same
// "interaction required" boundary the REST surface raises. The provider
// stops its connect loop on it (see `isSignInNeeded`) and lets the
// existing reauth gate do the redirect. The provider also reuses this
// function to authorize the gap report.
// ---------------------------------------------------------------------------

interface AcquireTokenDeps {
    readonly instance: PublicClientApplication;
    readonly config: AppConfig;
}

export async function acquireAccessToken(deps: AcquireTokenDeps): Promise<string> {
    const account: AccountInfo | null =
        deps.instance.getActiveAccount() ?? deps.instance.getAllAccounts()[0] ?? null;
    if (account === null) {
        throw new InteractionRequiredError(
            new Error(
                "realtime.acquireAccessToken: no active account — user is not signed in.",
            ),
        );
    }

    try {
        const result = await deps.instance.acquireTokenSilent({
            scopes: [deps.config.msal.apiScope],
            account,
        });
        return result.accessToken;
    } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
            throw new InteractionRequiredError(err);
        }
        throw err;
    }
}

// ---------------------------------------------------------------------------
// Sign-in detection
//
// SignalR calls `accessTokenFactory` inside negotiate. It catches every
// error there and throws a new one, with a message built from the
// original ("Failed to complete negotiation with the server: " plus the
// original error as text). So `instanceof InteractionRequiredError` is
// false on the error that `start()` rejects with, and on the
// `retryReason` that the retry policy gets. This check also looks for
// the original's text, "InteractionRequiredError: ...", in the message.
// ---------------------------------------------------------------------------

const SIGN_IN_ERROR_TEXT = "InteractionRequiredError:";

export function isSignInNeeded(error: unknown): boolean {
    return (
        error instanceof InteractionRequiredError ||
        (error instanceof Error && error.message.includes(SIGN_IN_ERROR_TEXT))
    );
}

// ---------------------------------------------------------------------------
// Reconnect timing
//
// Attempt 0 is immediate. Later attempts wait a random time between half
// of the ceiling and the ceiling (equal jitter), so tabs that lost the hub
// together do not retry together. The ceiling starts at 2 s, doubles on
// each attempt, and stops at 30 s. The provider's connect loop uses the
// same schedule.
//
// The retry policy never returns `null`, except when the user must sign
// in. A client that gives up cannot heal.
// ---------------------------------------------------------------------------

export const RECONNECT_BASE_MS = 2_000;
export const RECONNECT_CAP_MS = 30_000;

export function reconnectDelayMs(
    attempt: number,
    random: () => number = Math.random,
): number {
    if (attempt <= 0) return 0;
    const ceiling = Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** (attempt - 1));
    return Math.round(ceiling / 2 + (random() * ceiling) / 2);
}

export const realtimeRetryPolicy: IRetryPolicy = {
    nextRetryDelayInMilliseconds: ({ previousRetryCount, retryReason }) =>
        isSignInNeeded(retryReason) ? null : reconnectDelayMs(previousRetryCount),
};

// ---------------------------------------------------------------------------
// HubConnection factory
//
// Three properties worth flagging:
//
//   1. `withAutomaticReconnect(realtimeRetryPolicy)` retries a dropped
//      connection at once, then with capped exponential backoff and
//      jitter. The policy never returns `null`, except when the user
//      must sign in. When the policy ends (sign-in), or the server
//      closes the connection for good, `onclose` fires and
//      `RealtimeProvider` starts the connection again with its own
//      connect loop. SignalR does not retry a failed first `start()`;
//      that loop does.
//
//   2. Transports are constrained to `WebSockets` only. Long polling
//      falls back invisibly and masks misconfiguration; for the WS-first
//      Azure SignalR Service deployment, anything else is a bug, not a
//      feature. If a future corp proxy genuinely blocks WS, we want a
//      hard failure visible in the indicator, not a degraded silent
//      polling loop chewing through requests.
//
//   3. `accessTokenFactory` runs on every (re)negotiate and is async, so
//      a transient MSAL silent-refresh delay simply slows the connect.
// ---------------------------------------------------------------------------

export interface CreateRealtimeConnectionDeps {
    readonly instance: PublicClientApplication;
    readonly config: AppConfig;
}

export function createRealtimeConnection(
    deps: CreateRealtimeConnectionDeps,
): HubConnection {
    return new HubConnectionBuilder()
        .withUrl(REALTIME_HUB_PATH, {
            transport: HttpTransportType.WebSockets,
            accessTokenFactory: () => acquireAccessToken(deps),
        })
        .withAutomaticReconnect(realtimeRetryPolicy)
        .configureLogging(
            import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning,
        )
        .build();
}
